import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GameView } from "../game/GameView";
import { WordIntro } from "../game/ui/WordIntro";
import { MissedWordReview } from "../game/ui/MissedWordReview";
import {
  computeBonuses,
  rewardSettingsForLesson,
  type GameConfig,
  type RunOutcome,
} from "../game/types";
import type { WordDTO } from "@shared/types";
import { ResultsScreen } from "../components/ResultsScreen";
import { useLocalProfile } from "../local/useLocalProfile";
import { recordRun } from "../local/profile";
import { loadGameSettings } from "../local/gameSettings";
import { SEED_LESSONS, getSeedLesson, type SeedLesson } from "../data/seed-lessons";
import { fadeOutMenuMusic, sfx, startMenuMusic, stopMenuMusic } from "../game/audio";
import { SINGLE_COURSE_LENGTH, SINGLE_COURSE_SEED } from "../game/courseConfig";

type Lesson = SeedLesson;

function speedForLesson(orderIndex: number): number {
  const base = 13;
  const step = 0.7;
  const max = 22;
  return Math.min(max, base + Math.max(0, orderIndex - 1) * step);
}

function playerLevel(coins: number): number {
  return 1 + Math.floor(coins / 200);
}

type Phase = "loading" | "intro" | "flyover" | "countdown" | "playing" | "review" | "results";
const START_FLYOVER_MS = 4800;

export function LessonPlay() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const nav = useNavigate();
  const { profile, setProfile } = useLocalProfile();
  const [phase, setPhase] = useState<Phase>("loading");
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [words, setWords] = useState<WordDTO[]>([]);
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);
  const [recorded, setRecorded] = useState(false);
  const [countdownLabel, setCountdownLabel] = useState<"3" | "2" | "1" | "GO!" | null>(null);

  // No profile? Punt to character creation.
  useEffect(() => {
    if (!profile) nav("/", { replace: true });
  }, [profile, nav]);

  // Bundled lessons — no API call needed.
  useEffect(() => {
    if (!lessonId) return;
    const found = getSeedLesson(lessonId);
    if (!found) {
      nav("/play", { replace: true });
      return;
    }
    setLesson(found);
    const sorted = profile ? sortBySrsLocal(found.words, profile.mastery) : found.words;
    setWords(sorted);
    setPhase(profile?.progress[found.id]?.runs ? "flyover" : "intro");
  }, [lessonId, profile?.id, nav]);

  const config = useMemo<GameConfig | null>(() => {
    if (!lesson || words.length === 0 || !profile) return null;
    const rewards = rewardSettingsForLesson(lesson.orderIndex, lesson.coinsCount);
    const settings = loadGameSettings();
    return {
      lessonId: lesson.id,
      words,
      theme: lesson.theme,
      timeOfDay: lesson.timeOfDay,
      questionsCount: lesson.questionsCount,
      objectsCount: lesson.objectsCount,
      coinsCount: rewards.coinsCount,
      coinPickupValue: rewards.coinPickupValue,
      quizCorrectCoins: rewards.quizCorrectCoins,
      courseLength: SINGLE_COURSE_LENGTH,
      seed: SINGLE_COURSE_SEED,
      avatar: profile.avatarConfig,
      challengeKinds: lesson.challengeKinds ?? null,
      upgrades: profile.upgrades,
      runSpeed: speedForLesson(lesson.orderIndex),
      movementMode: settings.movementMode,
      movementSensitivity: settings.movementSensitivity,
      cameraSensitivity: settings.cameraSensitivity,
      controllerDeadzone: settings.controllerDeadzone,
      startPaused: true,
      startCameraFlyover: true,
    };
  }, [lesson, words, profile]);

  const handleEnd = (o: RunOutcome) => {
    setOutcome(o);
    const missed = o.wordOutcomes.filter((w) => !w.correct);
    if (missed.length > 0 && o.finished) setPhase("review");
    else setPhase("results");
  };

  // Persist to local profile when we reach the results screen.
  useEffect(() => {
    if (phase !== "results" || !outcome || !lesson || !profile || recorded) return;
    setRecorded(true);
    const bonus = computeBonuses(outcome);
    const next = recordRun(profile, {
      lessonId: lesson.id,
      score: bonus.totalScore,
      timeMs: outcome.timeMs,
      coinsEarned: bonus.totalCoins,
      hearts: outcome.hearts,
      wordOutcomes: outcome.wordOutcomes,
      finished: outcome.finished,
    });
    if (playerLevel(next.coins) > playerLevel(profile.coins)) sfx.levelUp();
    setProfile(next);
  }, [phase, outcome, lesson, profile, recorded, setProfile]);

  const handleReviewDone = (corrections: { wordId: string; correct: boolean }[]) => {
    if (!outcome) return;
    const final = outcome.wordOutcomes.map((w) => {
      if (w.correct) return w;
      const last = [...corrections].reverse().find((c) => c.wordId === w.wordId);
      return last ? { wordId: w.wordId, correct: last.correct } : w;
    });
    setOutcome({ ...outcome, wordOutcomes: final });
    setPhase("results");
  };

  useEffect(() => {
    if (phase === "intro") {
      startMenuMusic();
      return;
    }
    if (phase === "flyover") {
      setCountdownLabel(null);
      startMenuMusic();
      fadeOutMenuMusic(START_FLYOVER_MS);
      const timer = window.setTimeout(() => setPhase("countdown"), START_FLYOVER_MS);
      return () => window.clearTimeout(timer);
    }
    if (phase === "countdown" || phase === "playing") stopMenuMusic();
    if (phase !== "countdown") {
      setCountdownLabel(null);
      return;
    }
    setCountdownLabel("3");
    const timers = [
      window.setTimeout(() => setCountdownLabel("2"), 1000),
      window.setTimeout(() => setCountdownLabel("1"), 2000),
      window.setTimeout(() => setCountdownLabel("GO!"), 3000),
      window.setTimeout(() => setPhase("playing"), 4000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  if (phase === "loading" || !lesson) return <div className="full-center">Loading…</div>;

  if (phase === "intro") {
    return (
      <div className="lesson-intro-page">
        <div className="lesson-intro-top">
        <Link to="/play" className="lesson-intro-back">
          ← Back
        </Link>
        <div>
          <p>Learn Words</p>
          <h1>{lesson.title}</h1>
        </div>
        </div>
        <WordIntro words={words} onDone={() => setPhase("flyover")} />
      </div>
    );
  }

  if ((phase === "flyover" || phase === "countdown" || phase === "playing") && config) {
    return (
      <GameView
        config={config}
        onEnd={handleEnd}
        paused={phase === "flyover" || phase === "countdown"}
        countdownLabel={countdownLabel}
        showPauseOverlay={phase === "playing"}
      />
    );
  }

  if (phase === "review" && outcome) {
    const missedIds = new Set(
      outcome.wordOutcomes.filter((w) => !w.correct).map((w) => w.wordId),
    );
    const missedWords = words.filter((w) => missedIds.has(w.id));
    return (
      <div className="game-root">
        <div
          className="game-canvas"
          style={{
            background: "radial-gradient(ellipse at top, #1c2541 0%, #0b132b 60%)",
            display: "grid",
            placeItems: "center",
          }}
        />
        <MissedWordReview missed={missedWords} allWords={words} onDone={handleReviewDone} />
      </div>
    );
  }

  if (phase === "results" && outcome && lesson) {
    const sorted = [...SEED_LESSONS].sort((a, b) => a.orderIndex - b.orderIndex);
    const myIdx = sorted.findIndex((l) => l.id === lesson.id);
    const next = myIdx >= 0 && myIdx < sorted.length - 1 ? sorted[myIdx + 1] : null;
    const playAgain = () => {
      setRecorded(false);
      setOutcome(null);
      setPhase("flyover");
    };
    const goNext = () => next && nav(`/play/lesson/${next.id}`, { replace: true });
    const goHome = () => nav("/play");

    return (
      <ResultsScreen
        outcome={outcome}
        hasNext={!!next}
        onPlayAgain={playAgain}
        onNext={goNext}
        onHome={goHome}
        avatar={profile?.avatarConfig}
      />
    );
  }

  return <div className="full-center">…</div>;
}

/** Sort lesson words by SRS priority using locally-stored mastery state. */
function sortBySrsLocal(
  words: WordDTO[],
  mastery: Record<string, { srsLevel: number; srsDueAt: number | null; timesSeen: number }>,
): WordDTO[] {
  const now = Date.now();
  const score = (w: WordDTO): number => {
    const m = mastery[w.id];
    if (!m || m.timesSeen === 0) return 0;
    if (m.srsLevel === 0) return 1;
    if (m.srsDueAt != null && m.srsDueAt <= now) return 2;
    const wait = m.srsDueAt ? Math.max(0, m.srsDueAt - now) : 0;
    return 3 + wait / 3_600_000 + m.srsLevel * 0.5;
  };
  return [...words].sort((a, b) => score(a) - score(b));
}
